import React from 'react';
import { Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import {
  ArrowRight, QrCode, ScanLine, Wallet, Clock, Percent, BadgePercent,
  Instagram, Gift, Users, Activity, MapPin, Check, Sparkles,
  CalendarDays, GraduationCap, Plane,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { ThemeToggle } from '@/components/ThemeToggle';
import { BrandLogo } from '@/components/BrandLogo';
import { trackEventOnce } from '@/lib/pixel';

const roleHome = (role) => (role === 'admin' ? '/admin' : '/app');

const STATS = [
  { icon: Percent, value: 'Kazandıkça', label: 'artan komisyon' },
  { icon: BadgePercent, value: '%10', label: 'müşteri indirimi' },
  { icon: Wallet, value: '₺0', label: 'üyelik ücreti' },
];

const STEPS = [
  { icon: QrCode, title: 'Üye olun', text: 'Kayıt olun, kişisel QR kodunuz anında oluşsun.' },
  { icon: ScanLine, title: 'Kasada okutun', text: 'Müşteriniz Bella Maison mağazasında QR kodunuzu okutsun.' },
  { icon: Wallet, title: 'Kazanın', text: 'Komisyonunuz onaylandıkça düzenli olarak ödenir.' },
];

const PERKS = [
  { icon: Clock, title: 'Düzenli aylık ödeme', text: 'Onaylanan hak edişleriniz her ay düzenli olarak hesabınıza ödenir.' },
  { icon: Instagram, title: 'Ayın Mimarı', text: 'Öne çıkan mimarlar sosyal medyada tanıtılır.' },
  { icon: Sparkles, title: 'Yıl sonu tasarım turu', text: 'En başarılı partnerlere özel ilham gezisi.' },
  { icon: Users, title: 'Davet primi', text: 'Meslektaşınızı davet edin, prim kazanın.' },
  { icon: Activity, title: 'Anlık takip', text: 'Kazancınızı ve seviyenizi canlı olarak izleyin.' },
  { icon: Gift, title: 'Kampanya avantajları', text: 'Döneme özel çarpan ve bonus kampanyaları.' },
];

const ECOSYSTEM = [
  { icon: CalendarDays, title: 'Özel etkinlikler & lansmanlar', text: 'Yeni koleksiyon lansmanları ve partnerlere özel davetlerde ilk siz olun.' },
  { icon: Users, title: 'Mimar buluşmaları', text: 'Meslektaşlarınızla düzenli networking buluşmalarında tanışın, ilham alın.' },
  { icon: GraduationCap, title: 'Atölye & eğitimler', text: 'Trend, malzeme ve tasarım atölyeleriyle kendinizi sürekli besleyin.' },
  { icon: Plane, title: 'Tasarım turları & geziler', text: 'En başarılı partnerlere özel yurt içi ve yurt dışı ilham gezileri.' },
];

export default function Landing() {
  const { user, loading } = useAuth();
  const { search } = useLocation();
  const navigate = useNavigate();

  if (!loading && user) return <Navigate to={roleHome(user.role)} replace />;

  const to = (path) => `${path}${search || ''}`;
  const fireLead = () => trackEventOnce('Lead', {}, 'lead');
  const goSignup = () => { fireLead(); navigate(to('/kayit')); };

  return (
    <div className="min-h-screen bg-page text-ink" data-testid="landing-page">
      {/* Sticky header */}
      <header className="sticky top-0 z-40 bg-page/85 backdrop-blur-md border-b border-line">
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 min-w-0">
            <BrandLogo className="text-ink" />
            <span className="hidden sm:inline text-ink-faint">·</span>
            <span className="hidden sm:inline text-sm text-ink-soft whitespace-nowrap">Mimar Partner</span>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <ThemeToggle />
            <Link to={to('/giris')} data-testid="header-login-btn"
              className="text-sm px-3 py-2 rounded-full text-ink-soft hover:text-ink transition-colors">
              Giriş Yap
            </Link>
            <Link to={to('/kayit')} onClick={fireLead} data-testid="header-signup-btn"
              className="text-sm px-4 py-2 rounded-full bg-ink text-ground font-semibold transition-transform active:scale-95">
              Kaydol
            </Link>
          </div>
        </div>
      </header>

      {/* Hero — full-bleed image with dark overlay */}
      <section className="relative min-h-[82vh] md:min-h-[88vh] flex items-end overflow-hidden" data-testid="hero-section">
        <img src="/landing-hero.jpg" alt="Bella Maison iç mekan — mimar dokunuşu"
          className="absolute inset-0 w-full h-full object-cover" loading="eager" />
        {/* legibility overlay (theme-independent, keeps white text readable) */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/45 to-black/25" />
        <div className="absolute inset-0 bg-black/20" />

        <div className="relative w-full max-w-6xl mx-auto px-5 md:px-8 pb-16 md:pb-24 pt-28">
          <div className="max-w-2xl fade-up">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-white/90 font-semibold mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-redx" /> BELLA MAISON · Mimar Partner
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-white drop-shadow-sm">
              Hep birlikte evleri güzelleştirmeye!
            </h1>
            <p className="text-white/85 text-base md:text-lg mt-6 max-w-xl leading-relaxed">
              Müşterinizi Bella Maison'a getirin, tasarımınız kazanca dönüşsün. Mimar Partner
              ortaklığıyla hem daha güzel evler, hem birlikte büyüyen bir kazanç.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <button onClick={goSignup} data-testid="hero-cta-btn"
                className="inline-flex items-center gap-2 bg-white text-ink px-6 py-3.5 rounded-full font-semibold transition-transform active:scale-95 hover:bg-white/90">
                Kurucu mimar olun <ArrowRight strokeWidth={1.5} className="w-4 h-4" />
              </button>
              <a href="#nasil-calisir" data-testid="hero-how-link"
                className="inline-flex items-center gap-2 px-5 py-3.5 rounded-full border border-white/40 text-white hover:bg-white/10 transition-colors text-sm font-medium">
                Nasıl çalışır?
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Numbers band */}
      <section className="bg-chrome text-onchrome">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-10 grid grid-cols-3 gap-6">
          {STATS.map((s) => (
            <div key={s.label} className="text-center" data-testid={`stat-${s.label}`}>
              <s.icon strokeWidth={1.25} className="w-5 h-5 mx-auto mb-2 text-gold" />
              <p className="font-serif text-3xl md:text-4xl">{s.value}</p>
              <p className="text-onchrome/70 text-xs mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="nasil-calisir" className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-20">
        <h2 className="font-serif text-3xl md:text-4xl text-ink text-center">Nasıl çalışır?</h2>
        <p className="text-ink-soft text-sm text-center mt-3 max-w-md mx-auto">Üç adımda kazanmaya başlayın.</p>
        <div className="grid md:grid-cols-3 gap-5 mt-12">
          {STEPS.map((s, i) => (
            <div key={s.title} className="relative bg-ground border border-line rounded-2xl p-7" data-testid={`step-${i + 1}`}>
              <span className="absolute top-6 right-6 font-serif text-4xl text-line">{i + 1}</span>
              <div className="w-11 h-11 rounded-full border border-line flex items-center justify-center mb-5">
                <s.icon strokeWidth={1.25} className="w-5 h-5 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-ink">{s.title}</h3>
              <p className="text-ink-soft text-sm mt-2 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Ecosystem — belonging to something bigger */}
      <section className="bg-chrome text-onchrome" data-testid="ecosystem-section">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-24 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Visual */}
          <div className="relative order-last lg:order-first">
            <div className="relative overflow-hidden rounded-3xl aspect-[4/5] sm:aspect-[16/11] lg:aspect-[4/5]">
              <img
                src="https://images.pexels.com/photos/18999478/pexels-photo-18999478.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=1200"
                alt="Mimar buluşması — Bella Maison etkinlikleri"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-chrome/70 via-transparent to-transparent" />
            </div>
            {/* Floating community badge */}
            <div className="absolute -bottom-5 left-5 sm:left-8 bg-ground text-ink rounded-2xl px-5 py-4 shadow-xl border border-line flex items-center gap-3" data-testid="ecosystem-badge">
              <div className="w-10 h-10 rounded-full bg-gold/15 flex items-center justify-center shrink-0">
                <CalendarDays strokeWidth={1.25} className="w-5 h-5 text-gold" />
              </div>
              <div>
                <p className="font-serif text-xl leading-none">Yıl boyu etkinlik</p>
                <p className="text-ink-soft text-xs mt-1">Buluşmalar · atölyeler · geziler</p>
              </div>
            </div>
          </div>

          {/* Narrative */}
          <div className="fade-up">
            <span className="mini-label text-gold">Sizi besleyen bir topluluk</span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-tight mt-4">
              Kazançtan fazlası: birlikte geliştiğiniz bir topluluk
            </h2>
            <p className="text-onchrome/70 text-base mt-5 leading-relaxed max-w-lg">
              Mimar Partner; etkinlikler, buluşmalar, atölyeler ve tasarım gezileriyle sizi hem
              mesleki hem sosyal olarak besleyen bir topluluk. Kazanırken öğrenir, ilham alır ve
              meslektaşlarınızla birlikte büyürsünüz.
            </p>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7 mt-10">
              {ECOSYSTEM.map((e) => (
                <div key={e.title} className="flex items-start gap-3.5" data-testid={`ecosystem-${e.title}`}>
                  <div className="w-10 h-10 shrink-0 rounded-full border border-onchrome/20 flex items-center justify-center">
                    <e.icon strokeWidth={1.25} className="w-5 h-5 text-gold" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-sm">{e.title}</h3>
                    <p className="text-onchrome/60 text-sm mt-1 leading-relaxed">{e.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={goSignup} data-testid="ecosystem-cta-btn"
              className="inline-flex items-center gap-2 bg-gold text-cream px-6 py-3.5 rounded-full font-semibold mt-11 transition-transform active:scale-95">
              Topluluğa katılın <ArrowRight strokeWidth={1.5} className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Tier teaser (no numbers on public page) */}
      <section className="bg-surface-2/60 border-y border-line">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-16 md:py-20 text-center" data-testid="tier-teaser">
          <div className="flex items-center justify-center gap-2 mb-6">
            {['bg-bronze', 'bg-silver', 'bg-gold', 'bg-diamond'].map((c, i) => (
              <React.Fragment key={c}>
                <span className={`w-3 h-3 rounded-full ${c}`} />
                {i < 3 && <span className="w-8 h-px bg-line" />}
              </React.Fragment>
            ))}
          </div>
          <h2 className="font-serif text-3xl md:text-4xl text-ink">Yükseldikçe kazancınız artar</h2>
          <p className="text-ink-soft text-base mt-4 max-w-xl mx-auto leading-relaxed">
            Bronze'dan Diamond'a 4 seviye — sattıkça oranınız artar. Detaylar üyelikte.
          </p>
        </div>
      </section>

      {/* Perks */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-20">
        <h2 className="font-serif text-3xl md:text-4xl text-ink text-center">Partner ayrıcalıkları</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {PERKS.map((p) => (
            <div key={p.title} className="flex items-start gap-4 bg-ground border border-line rounded-2xl p-6">
              <div className="w-10 h-10 shrink-0 rounded-full bg-surface-2 flex items-center justify-center">
                <p.icon strokeWidth={1.25} className="w-5 h-5 text-gold" />
              </div>
              <div className="min-w-0">
                <h3 className="text-ink font-semibold text-sm">{p.title}</h3>
                <p className="text-ink-soft text-sm mt-1 leading-relaxed">{p.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Closing CTA */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 pb-20">
        <div className="relative overflow-hidden rounded-3xl bg-chrome text-onchrome px-8 py-14 md:py-16 text-center">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-gold font-semibold mb-5">
            <Sparkles strokeWidth={1.5} className="w-4 h-4" /> Sınırlı kontenjan
          </span>
          <h2 className="font-serif text-3xl md:text-5xl leading-tight max-w-2xl mx-auto">
            İlk 100 mimardan biri ol
          </h2>
          <p className="text-onchrome/70 text-sm mt-4 max-w-md mx-auto">
            90 gün boyunca en yüksek kazanç seviyesiyle başla!
          </p>
          <button onClick={goSignup} data-testid="closing-cta-btn"
            className="inline-flex items-center gap-2 bg-gold text-cream px-7 py-3.5 rounded-full font-semibold mt-8 transition-transform active:scale-95">
            Kaydol <ArrowRight strokeWidth={1.5} className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-line">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <BrandLogo className="text-ink" />
            <span className="text-ink-faint text-sm">— Mimar Partner</span>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <a href="mailto:partner@bellamaison.com" data-testid="footer-email"
              className="text-ink-soft hover:text-ink transition-colors">partner@bellamaison.com</a>
            <Link to={to('/giris')} className="text-ink-soft hover:text-ink transition-colors">Giriş Yap</Link>
          </div>
        </div>
        <p className="text-center text-ink-faint text-xs pb-8">© {new Date().getFullYear()} Bella Maison. Tüm hakları saklıdır.</p>
      </footer>
    </div>
  );
}
